import React, { useEffect, useState } from 'react';
import {
    Grid,
    Typography,
    IconButton,
    CircularProgress,
    Container,
    Card,
    CardActionArea,
    CardContent,
    Pagination
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const FolderFragment = ({ onStudentsClick, onCoursesClick }) => {
    const [currentView, setCurrentView] = useState('mainMenu');
    const [currentData, setCurrentData] = useState([]);
    const [previousViews, setPreviousViews] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [companies, setCompanies] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [dataType, setDataType] = useState(null);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [selectedDepartment, setSelectedDepartment] = useState(null);

    const itemsPerPage = 10;

    const mainMenuData = [
        { _id: '1', name: 'Students', nextView: 'categoryCompany' },
        { _id: '2', name: 'Courses', nextView: 'categoryCompany' }
    ];

    const dataMap = {
        categoryCompany: [],
        department: [],
        designation: [],
        role: []
    };

    const [data, setData] = useState(dataMap);

    const fetchData = async (schema) => {
        setIsLoading(true);
        try {
            const response = await fetch(`https://beiguru.co.in/${schema}`, {
                method: "GET",
                headers: { 'Authorization': localStorage.getItem('token') },
            });
            if (response.status === 401) {
                localStorage.clear();
                window.location.reload();
            } else {
                const result = await response.json();
                setData((prevData) => ({
                    ...prevData,
                    [schema]: result
                }));
                if (schema === currentView) {
                    setCurrentData(result);
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const fetchCompanies = async (page) => {
        const myHeaders = new Headers();
        myHeaders.append("Authorization", localStorage.getItem('token'));
        const requestOptions = {
            method: "GET",
            headers: myHeaders,
            redirect: "follow"
        };
        setIsLoading(true);
        try {
            const response = await fetch(`https://beiguru.co.in/admin/all-companies?page=${page}&limit=${itemsPerPage}`, requestOptions);
            if (response.status === 401) {
                localStorage.clear();
                window.location.reload();
            } else {
                const result = await response.json();
                setCompanies(result.companies);
                setTotalPages(result.totalPages);
                setCurrentPage(result.currentPage);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (currentView === 'companies') {
            fetchCompanies(currentPage);
        } else if (currentView !== 'mainMenu' && currentView !== 'companies') {
            fetchData(currentView);
        }
    }, [currentView, currentPage]);

    const handleNavigate = (nextView, item) => {
        setPreviousViews([...previousViews, { view: currentView, data: currentData }]);
        setCurrentView(nextView);
        if (nextView === 'categoryCompany') {
            setDataType(item._id);
        } else if (nextView === 'companies') {
            fetchCompanies(1);
        } else if (nextView === 'department') {
            setSelectedCompany(item);
        } else if (nextView === 'designation') {
            setSelectedDepartment(item);
        } else if (nextView === 'result') {
            // Decide based on whether Students or Courses is selected
            if (selectedCompany && selectedDepartment) {
                if (dataType === '1') {
                    // Trigger onStudentsClick for Students view
                    onStudentsClick(selectedCompany._id, selectedDepartment._id);
                } else {
                    // Trigger onCoursesClick for Courses view
                    onCoursesClick(selectedCompany._id, selectedDepartment._id);
                }
            }
            setCurrentView('employees');
        }
        if (nextView !== 'result') {
            setCurrentData(data[nextView] || []);
        }
    };

    const handleBack = () => {
        const lastView = previousViews.pop();
        setPreviousViews(previousViews);
        if (lastView) {
            setCurrentView(lastView.view);
            setCurrentData(lastView.data);
        }
    };

    const renderGridItems = () => {
        const items = currentView === 'mainMenu' ? mainMenuData : currentData;
        return (
            <Grid container spacing={3}>
                {items.map((item) => (
                    <Grid
                        item
                        xs={12}
                        sm={6}
                        md={2}
                        key={item._id}
                    >
                        <Card
                            sx={{
                                textAlign: 'center',
                                boxShadow: 0,
                                '&:hover': { boxShadow: 6, transform: 'scale(1.05)' },
                                transition: 'all 0.2s ease-in-out'
                            }}
                        >
                            <CardActionArea onClick={() => handleNavigate(item.nextView || getNextView(currentView), item)}>
                                <CardContent>
                                    <FolderIcon fontSize="large" color="primary" />
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ mt: 1 }}
                                    >
                                        {item.name}
                                    </Typography>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        );
    };

    const renderCompanies = () => (
        <Grid container spacing={3}>
            {companies.map((company) => (
                <Grid item xs={12} sm={6} md={4} key={company._id}>
                    <Card
                        sx={{
                            textAlign: 'center',
                            boxShadow: 0,
                            '&:hover': { boxShadow: 6, transform: 'scale(1.05)' },
                            transition: 'all 0.2s ease-in-out'
                        }}
                    >
                        <CardActionArea onClick={() => handleNavigate('department', company)}>
                            <CardContent>
                                <FolderIcon fontSize="large" color="primary" />
                                <Typography variant="subtitle1" sx={{ mt: 1 }}>
                                    {company.name}
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                </Grid>
            ))}
            <Grid item xs={12}>
                <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}
                />
            </Grid>
        </Grid>
    );

    const getNextView = (view) => {
        switch (view) {
            case 'categoryCompany': return 'companies';
            case 'companies': return 'department';
            case 'department': return 'designation';
            case 'designation': return 'role';
            case 'role': return 'result';
        }
    };

    return (
        <Grid item xs={10} style={{ padding: '20px', height: '100%', fontFamily: "Noto Sans", fontWeight: '500', justifyContent: 'center', alignContent: 'center', alignItems: 'center', display: 'flex', flexDirection: 'column' }}>
            <Container fluid style={{ height: '100%' }}>
                <Grid container spacing={3} direction="column">
                    <Grid item sx={{ flexDirection: 'row', display: 'flex', alignItems: 'center' }}>
                        {previousViews.length > 0 && (
                            <IconButton
                                onClick={handleBack}
                                color="primary"
                                sx={{ backgroundColor: 'white', '&:hover': { backgroundColor: '#e0e0e0' } }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                        )}
                        <Typography
                            variant="h5"
                            sx={{ textTransform: 'capitalize' }}
                        >Select Folder
                        </Typography>
                    </Grid>
                    <Grid item>
                        {isLoading ? (
                            <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />
                        ) : (
                            currentView === 'companies' ? renderCompanies() : renderGridItems()
                        )}
                    </Grid>
                </Grid>
            </Container>
        </Grid>
    );
};

export default FolderFragment;